import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { GroupingWeldsQuery, CreateWeldInspectionGroupsMutation, WeldInspectionGroupsQuery, DestroyWeldInspectionGroupMutation, RemoveWeldFromInspectionGroupMutation, DestroyAllWeldInspectionGroupsMutation, CreateExportWeldsList } from '../mutations'
import GroupingConfigurationTab from './GroupingConfigurationTab'
import { RiseLoader } from 'react-spinners'
import { GET_JOBS } from '../../JobsNotifications/graphql'
import { withApollo } from 'react-apollo'

const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <Modal isOpen toggle={onCancel}>
    <ModalHeader toggle={onCancel}>Confirm</ModalHeader>
    <ModalBody>{message}</ModalBody>
    <ModalFooter>
      <Button color="danger" onClick={onConfirm}>Yes</Button>
      <Button color="secondary" onClick={onCancel}>No</Button>
    </ModalFooter>
  </Modal>
)

const GROUP_BY_OPTIONS = [
  { key: 'length', label: 'Length', getValue: w => w.length },
  { key: 'thickness_1', label: 'Thickness 1', getValue: w => w.thickness_1 },
  { key: 'diameter_1', label: 'Diameter 1', getValue: w => w.diameter_1 },
  { key: 'thickness_2', label: 'Thickness 2', getValue: w => w.thickness_2 },
  { key: 'diameter_2', label: 'Diameter 2', getValue: w => w.diameter_2 },
  { key: 'material_1', label: 'Material 1', getValue: w => w.material_1 ? w.material_1.id : null },
  { key: 'material_2', label: 'Material 2', getValue: w => w.material_2 ? w.material_2.id : null },
  { key: 'weld_type', label: 'Weld Type', getValue: w => w.weld_joint_type ? w.weld_joint_type.map(j => `${j.weld_type ? j.weld_type.name : ''}${j.joint_type ? '-' + j.joint_type.name : ''}`).join(',') : null }
]

class GroupingWeldsTab extends Component {
  static propTypes = {
    parentId: PropTypes.number.isRequired,
    data: PropTypes.object.isRequired,
    inspCatTypesDict: PropTypes.array.isRequired,
    inspCatList: PropTypes.array.isRequired
  }

  constructor(props) {
    super(props)
    const { groupingState = {} } = props
    this.state = {
      selectedTypeId: groupingState.selectedTypeId || '',
      manageTypeId: '',
      minWelds: groupingState.minWelds || 2,
      groupByKeys: groupingState.groupByKeys || {},
      expandedGroups: {},
      expandedManageGroups: {},
      generating: false,
      confirmAction: null,
      toolbarHeight: 0,
      activeTab: 'generate',
      exportGroupsModalOpen: false,
      exportGroupsFormat: 'xlsx'
    }
    this.toolbarRef = React.createRef()
    this.setGroupingState = this.setGroupingState.bind(this)
  }

  setGroupingState(patch) {
    this.setState(patch, () => {
      const { selectedTypeId, minWelds, groupByKeys } = this.state
      this.props.onGroupingStateChange && this.props.onGroupingStateChange({ selectedTypeId, minWelds, groupByKeys })
    })
  }

  componentDidUpdate() {
    if (this.toolbarRef.current) {
      const h = this.toolbarRef.current.offsetHeight
      if (h !== this.state.toolbarHeight) this.setState({ toolbarHeight: h })
    }
  }

  getPartialInspectionTypes() {
    const { inspCatList, inspCatTypesDict } = this.props
    const typeIds = new Set()
    inspCatList.forEach(cat => {
      cat.inspections.forEach(insp => {
        if (insp.range < 100) typeIds.add(String(insp.type_id))
      })
    })
    return inspCatTypesDict.filter(t => typeIds.has(String(t.id)))
  }

  getGroupKey(weld) {
    const { groupByKeys } = this.state
    return GROUP_BY_OPTIONS
      .filter(o => groupByKeys[o.key])
      .map(o => `${o.key}:${o.getValue(weld)}`)
      .join('|')
  }

  buildGroups(welds) {
    const { selectedTypeId, minWelds } = this.state
    const { inspCatList, data } = this.props
    const minGroupingWeldsLength = data.element ? data.element.min_grouping_welds_length : null

    const assignedWeldIds = new Set(
      (this.props.groupsData.weld_inspection_groups || [])
        .filter(g => String(g.inspection_category_type_id) === String(selectedTypeId))
        .flatMap(g => g.weld_ids.map(String))
    )
    welds = welds.filter(w => !assignedWeldIds.has(String(w.id)))

    if (minGroupingWeldsLength != null) {
      welds = welds.filter(w => parseFloat(w.length) < minGroupingWeldsLength)
    }

    const byCat = {}
    welds.forEach(weld => {
      const catId = weld.inspection_category ? weld.inspection_category.id : 'none'
      if (!byCat[catId]) byCat[catId] = { category: weld.inspection_category, welds: [] }
      byCat[catId].welds.push(weld)
    })

    const groups = []
    let groupId = 1

    Object.values(byCat).forEach(({ category, welds: catWelds }) => {
      if (!category) return
      const catDef = inspCatList.find(c => String(c.id) === String(category.id))
      if (!catDef) return
      const insp = catDef.inspections.find(i => String(i.type_id) === String(selectedTypeId))
      if (!insp || insp.range >= 100) return

      const groupSize = Math.round(100 / insp.range)
      const range = insp.range
      const hasExtraGroupBy = Object.values(this.state.groupByKeys).some(Boolean)

      if (hasExtraGroupBy) {
        const byKey = {}
        catWelds.forEach(weld => {
          const key = this.getGroupKey(weld)
          if (!byKey[key]) byKey[key] = []
          byKey[key].push(weld)
        })
        Object.values(byKey).forEach(keyWelds => {
          for (let i = 0; i < keyWelds.length; i += groupSize) {
            const slice = keyWelds.slice(i, i + groupSize)
            if (slice.length >= minWelds) groups.push({ id: groupId++, category, welds: slice, range, groupSize })
          }
        })
      } else {
        for (let i = 0; i < catWelds.length; i += groupSize) {
          const slice = catWelds.slice(i, i + groupSize)
          if (slice.length >= minWelds) groups.push({ id: groupId++, category, welds: slice, range, groupSize })
        }
      }
    })

    return groups
  }

  getRepresentativeWeld(group) {
    return group.welds.reduce((max, w) => (w.length > max.length ? w : max), group.welds[0])
  }

  getExtendedScope(group) {
    return group.welds.length * group.range
  }

  getRequiredLength(group) {
    const weld = this.getRepresentativeWeld(group)
    const scope = this.getExtendedScope(group)
    return Math.round(weld.length * scope / 100)
  }

  getWeldTypeName(weld) {
    if (!weld.weld_joint_type || !weld.weld_joint_type.length) return '-'
    return weld.weld_joint_type.map(j => {
      const wt = j.weld_type ? j.weld_type.name : ''
      const jt = j.joint_type ? `-${j.joint_type.name}` : ''
      return `${wt}${jt}`
    }).join(', ')
  }

  handleGenerateGroups(groups) {
    this.setState({ generating: true })
    const { selectedTypeId, groupByKeys, minWelds } = this.state
    const variables = {
      project_id: this.props.parentId,
      inspection_category_type_id: parseInt(selectedTypeId),
      group_by_keys: JSON.stringify(groupByKeys),
      min_welds: minWelds,
      groups: groups.map(group => {
        const rep = this.getRepresentativeWeld(group)
        return {
          weld_ids: group.welds.map(w => parseInt(w.id)),
          representative_weld_id: parseInt(rep.id),
          scope_percent: this.getExtendedScope(group),
          required_length: this.getRequiredLength(group)
        }
      })
    }
    this.props.createWeldInspectionGroups({ variables })
      .then(() => Promise.all([this.props.data.refetch(), this.props.groupsData.refetch()]))
      .then(() => this.setState({ generating: false }))
      .catch(err => { console.error('Generate groups error', err); this.setState({ generating: false }) })
  }

  handleDestroyGroup(groupId) {
    this.setState({ confirmAction: { type: 'destroyGroup', groupId, message: 'Remove this group?' } })
  }

  handleRemoveWeld(groupId, weldId) {
    this.setState({ confirmAction: { type: 'removeWeld', groupId, weldId, message: 'Remove this weld from the group?' } })
  }

  handleExportGroups(format) {
    const args = {
      format,
      version: 'welds',
      project_id: this.props.parentId,
      inspection_category_type_id: parseInt(this.state.manageTypeId)
    }
    this.props.createExportJob({ variables: { type: 'WlWelds::Jobs::ExportWeldsList', args: JSON.stringify(args) } })
      .then(() => {
        this.props.client.query({ query: GET_JOBS, fetchPolicy: 'network-only' }).then(({ data }) => {
          const notifyCount = data.user_jobs ? data.user_jobs.filter(job => job.notify).length : 0
          window.dispatchEvent(new CustomEvent('changeNotifyCountInApp', { detail: { notifyCount } }))
        })
        this.setState({ exportGroupsModalOpen: false })
      })
      .catch(err => console.error('Export groups error', err))
  }

  handleDestroyAll() {
    this.setState({ confirmAction: { type: 'destroyAll', message: 'Remove all groups for this inspection type? Only welds without inspections will be ungrouped.' } })
  }

  executeConfirm() {
    const { confirmAction } = this.state
    this.setState({ confirmAction: null })
    if (confirmAction.type === 'destroyGroup') {
      this.props.destroyWeldInspectionGroup({ variables: { id: confirmAction.groupId } })
        .then(() => this.props.groupsData.refetch())
        .catch(err => console.error('DestroyGroup error', err))
    } else if (confirmAction.type === 'removeWeld') {
      this.props.removeWeldFromInspectionGroup({ variables: { group_id: confirmAction.groupId, weld_id: confirmAction.weldId } })
        .then(() => this.props.groupsData.refetch())
        .catch(err => console.error('RemoveWeld error', err))
    } else if (confirmAction.type === 'destroyAll') {
      this.setState({ generating: true })
      this.props.destroyAllWeldInspectionGroups({
        variables: { project_id: this.props.parentId, inspection_category_type_id: parseInt(this.state.manageTypeId) }
      })
        .then(() => this.props.groupsData.refetch())
        .then(() => this.setState({ generating: false }))
        .catch(err => { console.error('DestroyAll error', err); this.setState({ generating: false }) })
    }
  }

  toggleGroup(id) {
    this.setState(prev => ({
      expandedGroups: { ...prev.expandedGroups, [id]: !prev.expandedGroups[id] }
    }))
  }

  render() {
    const { data, inspCatTypesDict } = this.props
    const { selectedTypeId, minWelds, groupByKeys, expandedGroups, toolbarHeight, activeTab } = this.state

    if (data.loading || this.state.generating) return <div style={{ textAlign: 'center', padding: '40px' }}><RiseLoader color="#20a8d8" loading={true} /></div>
    if (data.error) return <div style={{ padding: '20px' }}>Error loading data: {data.error.message}</div>

    const welds = data.grouping_welds || []
    const partialTypes = this.getPartialInspectionTypes()
    const groups = selectedTypeId && !this.props.groupsData.loading ? this.buildGroups(welds) : []

    return (
      <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        <ul className="nav nav-tabs" style={{ padding: '0 16px', background: '#fff' }}>
          <li className="nav-item">
            <a className={`nav-link${activeTab === 'generate' ? ' active' : ''}`} href="#" onClick={e => { e.preventDefault(); this.setState({ activeTab: 'generate' }) }}>Generate groups</a>
          </li>
          <li className="nav-item">
            <a className={`nav-link${activeTab === 'manage' ? ' active' : ''}`} href="#" onClick={e => { e.preventDefault(); this.setState({ activeTab: 'manage' }) }}>Manage groups</a>
          </li>
          <li className="nav-item">
            <a className={`nav-link${activeTab === 'configuration' ? ' active' : ''}`} href="#" onClick={e => { e.preventDefault(); this.setState({ activeTab: 'configuration' }) }}>Grouping configuration</a>
          </li>
        </ul>
        {activeTab === 'configuration' && (
          <GroupingConfigurationTab parentId={this.props.parentId} />
        )}
        {activeTab === 'manage' && (
          <React.Fragment>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <select
                className="form-control"
                style={{ width: '200px' }}
                value={this.state.manageTypeId}
                onChange={e => this.setState({ manageTypeId: e.target.value, expandedManageGroups: {} })}
              >
                <option value=''>-- Select inspection type --</option>
                {partialTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {this.state.manageTypeId && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button color="secondary" onClick={() => this.setState({ exportGroupsModalOpen: true })}>
                    <i className="fa fa-download" style={{ marginRight: '6px' }} />Export
                  </Button>
                  <Button color="danger" onClick={() => this.handleDestroyAll()}>
                    <i className="fa fa-trash" style={{ marginRight: '6px' }} />Remove groups
                  </Button>
                </div>
              )}
            </div>
            {!this.state.manageTypeId ? (
              <div style={{ padding: '20px', color: '#999' }}>Select an inspection type to view groups.</div>
            ) : this.props.groupsData.loading ? (
              <div style={{ padding: '20px' }} />
            ) : (() => {
              const weldInspById = {}
              const weldByIdFromGroups = {}
              ;(this.props.groupsData.weld_inspection_groups || []).forEach(g => {
                if (g.welds_inspections) {
                  JSON.parse(g.welds_inspections).forEach(w => {
                    if (!weldInspById[String(w.id)]) {
                      weldInspById[String(w.id)] = w.inspections ? (typeof w.inspections === 'string' ? JSON.parse(w.inspections) : w.inspections) : {}
                    }
                  })
                }
                if (g.welds_data) {
                  JSON.parse(g.welds_data).forEach(w => {
                    weldByIdFromGroups[String(w.id)] = w
                  })
                }
              })
              const weldById = { ...weldByIdFromGroups }
              welds.forEach(w => { weldById[String(w.id)] = w })
              const filtered = (this.props.groupsData.weld_inspection_groups || [])
                .filter(g => String(g.inspection_category_type_id) === String(this.state.manageTypeId))
                .sort((a, b) => a.id - b.id)
              if (!filtered.length) return <div style={{ padding: '20px', color: '#999' }}>No groups for selected inspection type.</div>
              return (
                <table className="table table-striped" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Group</th>
                      <th>Welds count</th>
                      <th>Representative weld</th>
                      <th>Required length</th>
                      <th style={{ width: '50px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((group, idx) => (
                      <React.Fragment key={group.id}>
                        <tr
                          style={{ cursor: 'pointer', backgroundColor: this.state.expandedManageGroups[group.id] ? '#e8f4f8' : '' }}
                          onClick={() => this.setState(prev => ({ expandedManageGroups: { ...prev.expandedManageGroups, [group.id]: !prev.expandedManageGroups[group.id] } }))}
                        >
                          <td><i className={`fa fa-chevron-${this.state.expandedManageGroups[group.id] ? 'down' : 'right'}`} style={{ marginRight: '8px' }} />Group {group.id}</td>
                          <td>{group.weld_ids.length}</td>
                          <td>{weldById[String(group.representative_weld_id)] ? weldById[String(group.representative_weld_id)].no : group.representative_weld_id}</td>
                          <td>{weldById[String(group.representative_weld_id)] ? weldById[String(group.representative_weld_id)].length : group.required_length} mm</td>
                          <td style={{ width: '50px' }}>
                            <Button color="danger" size="sm" onClick={e => { e.stopPropagation(); this.handleDestroyGroup(group.id) }}>
                              <i className="fa fa-trash" />
                            </Button>
                          </td>
                        </tr>
                        {this.state.expandedManageGroups[group.id] && (
                          <tr>
                            <td colSpan={5} style={{ padding: 0 }}>
                              <table className="table table-sm" style={{ marginBottom: 0, backgroundColor: '#f5fbfd' }}>
                                <thead>
                                  <tr>
                                    <th>No</th>
                                    <th>Status</th>
                                    <th>Weld Type</th>
                                    <th>Length</th>
                                    <th>Thickness 1</th>
                                    <th>Diameter 1</th>
                                    <th>Thickness 2</th>
                                    <th>Diameter 2</th>
                                    <th>Material 1</th>
                                    <th>Material 2</th>
                                    <th style={{ width: '50px' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.weld_ids.map(wid => {
                                    const weld = weldById[String(wid)]
                                    if (!weld) return null
                                    const isRep = String(weld.id) === String(group.representative_weld_id)
                                    const alreadyInspected = weldInspById[String(wid)] && weldInspById[String(wid)][String(this.state.manageTypeId)]

                                    return (
                                      <tr key={weld.id} style={{ fontWeight: isRep ? 'bold' : 'normal' }}>
                                        <td>{weld.no}{isRep && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#20a8d8' }}>★ representative</span>}</td>
                                        <td>{weld.status || '-'}</td>
                                        <td>{this.getWeldTypeName(weld)}</td>
                                        <td>{weld.length}</td>
                                        <td>{weld.thickness_1}</td>
                                        <td>{weld.diameter_1 || '-'}</td>
                                        <td>{weld.thickness_2 || '-'}</td>
                                        <td>{weld.diameter_2 || '-'}</td>
                                        <td>{weld.material_1 ? weld.material_1.designation_name : '-'}</td>
                                        <td>{weld.material_2 ? weld.material_2.designation_name : '-'}</td>
                                        <td style={{ width: '50px' }}>
                                          {!isRep && (
                                            <i className="fa fa-times" style={{ color: '#f86c6b', cursor: 'pointer', fontSize: '16px' }} onClick={() => this.handleRemoveWeld(group.id, weld.id)} />
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )
            })()}
          </React.Fragment>
        )}
        {activeTab === 'generate' && <React.Fragment>
          <div ref={this.toolbarRef} style={{ padding: '10px 16px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
          <div>
            <strong>Build group by</strong>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <select
                className="form-control"
                style={{ width: '200px' }}
                value={selectedTypeId}
                onChange={e => this.setState({ selectedTypeId: e.target.value, expandedGroups: {} })}
              >
                <option value=''>-- Select inspection type --</option>
                {partialTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {GROUP_BY_OPTIONS.map(o => (
                <label key={o.key} style={{ cursor: 'pointer', marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={!!groupByKeys[o.key]}
                    onChange={() => this.setState(prev => {
                      const groupByKeys = { ...prev.groupByKeys, [o.key]: !prev.groupByKeys[o.key] }
                      const next = { groupByKeys, expandedGroups: {} }
                      setTimeout(() => this.props.onGroupingStateChange && this.props.onGroupingStateChange({ selectedTypeId: this.state.selectedTypeId, minWelds: this.state.minWelds, groupByKeys }), 0)
                      return next
                    })}
                    style={{ marginRight: '6px' }}
                  />
                  {o.label}
                </label>
              ))}
            </div>
            <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              Min welds in group:
              <select
                className="form-control"
                style={{ width: '80px' }}
                value={minWelds}
                onChange={e => this.setState({ minWelds: parseInt(e.target.value), expandedGroups: {} })}
              >
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>
          {selectedTypeId && groups.length > 0 && (
            <Button color="primary" onClick={() => this.handleGenerateGroups(groups)}>
              <i className="fa fa-plus" style={{ marginRight: '6px' }} />Generate groups ({groups.length})
            </Button>
          )}
        </div>

        {!selectedTypeId ? (
          <div style={{ padding: '20px', color: '#999' }}>Select an inspection type to build groups.</div>
        ) : (
          <table className="table table-striped" style={{ marginBottom: 0 }}>
            <thead style={{ position: 'sticky', top: toolbarHeight, zIndex: 1, backgroundColor: '#fff' }}>
              <tr>
                <th>Group</th>
                <th>Inspection Category</th>
                <th>Welds count</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', color: '#999' }}>No groups to build</td></tr>
              ) : groups.map((group, idx) => (
                <React.Fragment key={group.id}>
                  <tr
                    style={{ cursor: 'pointer', backgroundColor: expandedGroups[group.id] ? '#e8f4f8' : '' }}
                    onClick={() => this.toggleGroup(group.id)}
                  >
                    <td><i className={`fa fa-chevron-${expandedGroups[group.id] ? 'down' : 'right'}`} style={{ marginRight: '8px' }} />Group</td>
                    <td>{group.category.name}</td>
                    <td>{group.welds.length}</td>
                  </tr>
                  {expandedGroups[group.id] && (
                    <tr>
                      <td colSpan={3} style={{ padding: 0 }}>
                        <table className="table table-sm" style={{ marginBottom: 0, backgroundColor: '#f5fbfd' }}>
                          <thead>
                            <tr>
                              <th>No</th>
                              <th>Status</th>
                              <th>Weld Type</th>
                              <th>Length</th>
                              <th>Thickness 1</th>
                              <th>Diameter 1</th>
                              <th>Thickness 2</th>
                              <th>Diameter 2</th>
                              <th>Material 1</th>
                              <th>Material 2</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.welds.map(weld => (
                              <tr key={weld.id} style={{ fontWeight: weld.id === this.getRepresentativeWeld(group).id ? 'bold' : 'normal' }}>
                                <td>{weld.no}{weld.id === this.getRepresentativeWeld(group).id && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#20a8d8' }}>★ representative</span>}</td>
                                <td>{weld.status || '-'}</td>
                                <td>{this.getWeldTypeName(weld)}</td>
                                <td>{weld.length}</td>
                                <td>{weld.thickness_1}</td>
                                <td>{weld.diameter_1 || '-'}</td>
                                <td>{weld.thickness_2 || '-'}</td>
                                <td>{weld.diameter_2 || '-'}</td>
                                <td>{weld.material_1 ? weld.material_1.designation_name : '-'}</td>
                                <td>{weld.material_2 ? weld.material_2.designation_name : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        </React.Fragment>
        }
        {this.state.exportGroupsModalOpen && (
          <Modal isOpen toggle={() => this.setState({ exportGroupsModalOpen: false })}>
            <ModalHeader toggle={() => this.setState({ exportGroupsModalOpen: false })}>Export Inspection Groups</ModalHeader>
            <ModalBody>
              <div className="form-check mb-2">
                <input id="grp-xlsx" className="form-check-input" type="radio" name="exportGroupsFormat"
                  checked={this.state.exportGroupsFormat === 'xlsx'}
                  onChange={() => this.setState({ exportGroupsFormat: 'xlsx' })} />
                <label htmlFor="grp-xlsx" className="form-check-label">Microsoft Excel (xlsx)</label>
              </div>
              <div className="form-check">
                <input id="grp-pdf" className="form-check-input" type="radio" name="exportGroupsFormat"
                  checked={this.state.exportGroupsFormat === 'pdf_inspection_groups'}
                  onChange={() => this.setState({ exportGroupsFormat: 'pdf_inspection_groups' })} />
                <label htmlFor="grp-pdf" className="form-check-label">PDF</label>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => this.setState({ exportGroupsModalOpen: false })}>Cancel</Button>
              <Button color="primary" onClick={() => this.handleExportGroups(this.state.exportGroupsFormat)}>Export</Button>
            </ModalFooter>
          </Modal>
        )}
        {this.state.confirmAction && (
          <ConfirmModal
            message={this.state.confirmAction.message}
            onConfirm={() => this.executeConfirm()}
            onCancel={() => this.setState({ confirmAction: null })}
          />
        )}
      </div>
    )
  }
}

export default withApollo(compose(
  graphql(GroupingWeldsQuery, {
    options: (props) => ({
      variables: { project_id: props.parentId },
      fetchPolicy: 'network-only'
    })
  }),
  graphql(WeldInspectionGroupsQuery, {
    name: 'groupsData',
    options: (props) => ({
      variables: { project_id: props.parentId },
      fetchPolicy: 'network-only'
    })
  }),
  graphql(CreateWeldInspectionGroupsMutation, {
    name: 'createWeldInspectionGroups'
  }),
  graphql(DestroyWeldInspectionGroupMutation, {
    name: 'destroyWeldInspectionGroup'
  }),
  graphql(RemoveWeldFromInspectionGroupMutation, {
    name: 'removeWeldFromInspectionGroup'
  }),
  graphql(DestroyAllWeldInspectionGroupsMutation, {
    name: 'destroyAllWeldInspectionGroups'
  }),
  graphql(CreateExportWeldsList, {
    name: 'createExportJob'
  })
)(GroupingWeldsTab))



// WEBPACK FOOTER //
// ./src/components/Projects/tabs/GroupingWeldsTab.js